import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ContentResultsContext = createContext(null);

const initialFeedState = {
  lastSeenMarkers: {},
  itemsViewed: [],
  feedbackItemPopup: {
    isOpen: false,
    isHandPicked: false,
    ratingScored: null,
    item: null,
    index: -1,
    keywords: [],
  },
  enforceReloadFeed: false,
  priorityBadgeModalState: {
    itemData: null,
    isOpen: false,
  },
};

/**
 * Exposes content results and feed-related state. State lives in context only (no store).
 * @param {Object} contentResultsInitialState - Optional initial contentResults for tests
 * @param {Object} initialState - Optional initial feed state overrides for tests
 */
export const ContentResultsProvider = ({ children, contentResultsInitialState, initialState }) => {
  const [contentResults, setContentResults] = useState(contentResultsInitialState ?? {});
  const [feedState, setFeedState] = useState(
    initialState ? { ...initialFeedState, ...initialState } : initialFeedState
  );

  const setFeedStateField = useCallback((arg1, arg2) => {
    if (typeof arg1 === 'function') {
      setFeedState(arg1);
    } else if (arg2 !== undefined) {
      setFeedState((prev) => ({ ...prev, [arg1]: arg2 }));
    } else {
      setFeedState((prev) => ({ ...prev, ...arg1 }));
    }
  }, []);

  const value = useMemo(
    () => ({
      contentResults,
      setContentResults,
      ...feedState,
      setFeedState: setFeedStateField,
    }),
    [contentResults, feedState, setFeedStateField]
  );

  return <ContentResultsContext.Provider value={value}>{children}</ContentResultsContext.Provider>;
};

export function useContentResults() {
  const context = useContext(ContentResultsContext);
  if (context == null) {
    throw new Error('useContentResults must be used within a ContentResultsProvider (inside Dashboard).');
  }
  return context;
}

export { initialFeedState };
export default ContentResultsContext;
